import type { ComputedRef, Ref } from 'vue'
import { computed, ref } from 'vue'
import {
  ConversationService,
  type Conversation,
  type ConversationListFilters,
  type ConversationListItem,
  type CreateConversationMessagePayload,
  type CreateConversationPayload,
  type StaticSubjectConversationType,
  type UpdateConversationMessagePayload,
} from '@staff/shared'

/**
 * This composable provides methods for fetching and managing conversations and messages related to applications and debts.
 * It is designed to be used through composable wrappers for periscope and microscope that provide the correct apiUrl and token for each application:
 * - usePeriscopeConversations
 * - useMicroscopeConversations
 */

type UseConversationsOptions = {
  apiUrl: string
  getToken: () => string | null | undefined
}

export type CreateConversationInput =
  | {
      type: 'other'
      subject: string
    }
  | {
      type: 'application'
      subject?: string
    }
  | {
      type: StaticSubjectConversationType
    }

// Tracks which conversation collection is currently loaded so single-conversation
// updates can be reconciled against the correct list and filters.
type ConversationListScope =
  | {
      kind: 'application'
      id: string
      filters?: ConversationListFilters
    }
  | {
      kind: 'debt'
      id: string
      filters?: ConversationListFilters
    }

const toConversationListItem = (conversation: Conversation): ConversationListItem => ({
  id: conversation.id,
  applicationId: conversation.applicationId,
  debtId: conversation.debtId,
  type: conversation.type,
  subject: conversation.subject,
  status: conversation.status,
  createdAt: conversation.createdAt,
  updatedAt: conversation.updatedAt,
  hasUnreadMessages: conversation.hasUnreadMessages,
  latestMessage: conversation.messages[conversation.messages.length - 1] ?? null,
})

const matchesConversationListFilters = (conversation: Conversation, filters?: ConversationListFilters) => {
  if (filters?.status && conversation.status !== filters.status) return false
  if (filters?.onlyUnread && !conversation.hasUnreadMessages) return false
  return true
}

export const useConversations = (options: UseConversationsOptions) => {
  const conversationsList = ref<ConversationListItem[]>([])
  const selectedConversationId = ref<string | null>(null)
  const selectedConversation = ref<Conversation | null>(null)
  const conversationError = ref<unknown>()
  const isLoadingConversations = ref(false)
  const activeConversationListRequests = ref(0)
  const isLoadingConversation = ref(false)
  const isCreatingConversation = ref(false)
  const isCreatingConversationMessage = ref(false)
  const isUpdatingConversation = ref(false)
  const isUpdatingConversationMessage = ref(false)
  const currentListScope = ref<ConversationListScope | null>(null)

  const resolveService = (): ConversationService => {
    const { apiUrl, getToken } = options
    const token = getToken()

    if (!token) {
      throw new Error('Missing token.')
    }

    return new ConversationService({ apiUrl, token })
  }

  const startLoadingConversationList = () => {
    activeConversationListRequests.value += 1
    isLoadingConversations.value = true
    conversationError.value = null
  }

  const finishLoadingConversationList = () => {
    activeConversationListRequests.value = Math.max(activeConversationListRequests.value - 1, 0)
    isLoadingConversations.value = activeConversationListRequests.value > 0
  }

  // Keep the sidebar list in sync from a full conversation response so mutations
  // do not need to refetch the entire application/debt conversation list.
  const syncConversationInList = (nextConversation: Conversation) => {
    const scope = currentListScope.value
    if (!scope) return

    const matchesScope =
      scope.kind === 'application' ? nextConversation.applicationId === scope.id : nextConversation.debtId === scope.id

    const remainingItems = conversationsList.value.filter((item) => item.id !== nextConversation.id)

    if (!matchesScope || !matchesConversationListFilters(nextConversation, scope.filters)) {
      conversationsList.value = remainingItems
      return
    }

    const nextItem = toConversationListItem(nextConversation)
    const existingIndex = conversationsList.value.findIndex((item) => item.id === nextConversation.id)

    if (existingIndex === -1) {
      conversationsList.value = [nextItem, ...remainingItems]
      return
    }

    conversationsList.value = conversationsList.value.map((item) => (item.id === nextConversation.id ? nextItem : item))
  }

  // Apply a full conversation response to both detail and list state in one place
  // so every mutation follows the same synchronization path.
  const applyConversationUpdate = (nextConversation: Conversation, options?: { setCurrentConversation?: boolean }) => {
    if (options?.setCurrentConversation || selectedConversationId.value === nextConversation.id) {
      selectedConversationId.value = nextConversation.id
      selectedConversation.value = nextConversation
    }

    syncConversationInList(nextConversation)
    return nextConversation
  }

  /**
   * Find most recent open conversation for first select.
   */
  const findMostRecentOpenConversation = (items: ConversationListItem[]) => {
    const openConversation = [...items]
      .filter((item) => item.status === 'open')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]

    return openConversation?.id ?? items[0]?.id
  }

  /**
   * Select a sensible default after loading a list while preserving the current
   * selection when it still exists in the updated list.
   */
  const selectDefaultConversation = async () => {
    const currentSelectionStillExists = conversationsList.value.some((item) => item.id === selectedConversationId.value)

    if (currentSelectionStillExists && selectedConversationId.value) {
      if (!selectedConversation.value || selectedConversation.value.id !== selectedConversationId.value) {
        await getConversation(selectedConversationId.value)
      }
      return
    }

    const defaultId = findMostRecentOpenConversation(conversationsList.value)
    await selectConversation(defaultId)
  }

  /**
   * Select a conversation to use.
   */
  const selectConversation = async (conversationId?: string) => {
    if (!conversationId) {
      selectedConversation.value = null
      selectedConversationId.value = null
      return
    }

    if (selectedConversationId.value === conversationId && selectedConversation.value?.id === conversationId) {
      return
    }

    selectedConversationId.value = conversationId
    await getConversation(conversationId)
  }

  /**
   * Fetch a single conversation with all its messages.
   */
  const getConversation = async (conversationId: string) => {
    isLoadingConversation.value = true
    conversationError.value = null

    try {
      const result = await resolveService().getConversation(conversationId)
      return applyConversationUpdate(result, { setCurrentConversation: true })
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      isLoadingConversation.value = false
    }
  }

  /**
   * Fetch all conversations for an application.
   */
  const getApplicationConversations = async (applicationId: string, filters?: ConversationListFilters) => {
    startLoadingConversationList()
    currentListScope.value = {
      kind: 'application',
      id: applicationId,
      filters,
    }

    try {
      const result = await resolveService().getApplicationConversations(applicationId, filters)
      conversationsList.value = result
      await selectDefaultConversation()
      return result
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      finishLoadingConversationList()
    }
  }

  /**
   * Fetch all conversations for a debt.
   */
  const getDebtConversations = async (debtId: string, filters?: ConversationListFilters) => {
    startLoadingConversationList()
    currentListScope.value = {
      kind: 'debt',
      id: debtId,
      filters,
    }

    try {
      const result = await resolveService().getDebtConversations(debtId, filters)
      conversationsList.value = result
      await selectDefaultConversation()
      return result
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      finishLoadingConversationList()
    }
  }

  /**
   * Fetch conversations across all accessible application and debt targets.
   */
  const getConversations = async (filters?: ConversationListFilters) => {
    startLoadingConversationList()

    try {
      return await resolveService().getConversations(filters)
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      finishLoadingConversationList()
    }
  }

  /**
   * Create a new conversation for an application or debt.
   */
  const createConversation = async (payload: CreateConversationPayload) => {
    isCreatingConversation.value = true
    conversationError.value = null

    try {
      const createdConversation = await resolveService().createConversation(payload)
      return applyConversationUpdate(createdConversation, { setCurrentConversation: true })
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      isCreatingConversation.value = false
    }
  }

  /**
   * Post a new message in an existing conversation.
   */
  const createConversationMessage = async (conversationId: string, payload: CreateConversationMessagePayload) => {
    isCreatingConversationMessage.value = true
    conversationError.value = null

    try {
      const updatedConversation = await resolveService().createConversationMessage(conversationId, payload)
      return applyConversationUpdate(updatedConversation)
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      isCreatingConversationMessage.value = false
    }
  }

  /**
   * Update a conversation message by editing its content or read state.
   */
  const updateConversationMessage = async (
    conversationId: string,
    messageId: string,
    payload: UpdateConversationMessagePayload,
  ) => {
    isUpdatingConversationMessage.value = true
    conversationError.value = null

    try {
      const updatedConversation = await resolveService().updateConversationMessage(conversationId, messageId, payload)
      return applyConversationUpdate(updatedConversation)
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      isUpdatingConversationMessage.value = false
    }
  }

  /**
   * Soft delete a conversation message.
   */
  const deleteConversationMessage = async (conversationId: string, messageId: string) => {
    isUpdatingConversationMessage.value = true
    conversationError.value = null

    try {
      const updatedConversation = await resolveService().deleteConversationMessage(conversationId, messageId)
      return applyConversationUpdate(updatedConversation)
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      isUpdatingConversationMessage.value = false
    }
  }

  /**
   * Close an existing conversation.
   */
  const closeConversation = async (conversationId: string) => {
    isUpdatingConversation.value = true
    conversationError.value = null

    try {
      const updatedConversation = await resolveService().closeConversation(conversationId)
      return applyConversationUpdate(updatedConversation)
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      isUpdatingConversation.value = false
    }
  }

  /**
   * Reopen an existing conversation.
   */
  const reopenConversation = async (conversationId: string) => {
    isUpdatingConversation.value = true
    conversationError.value = null

    try {
      const updatedConversation = await resolveService().reopenConversation(conversationId)
      return applyConversationUpdate(updatedConversation)
    } catch (err: unknown) {
      conversationError.value = err
    } finally {
      isUpdatingConversation.value = false
    }
  }

  return {
    conversationsList,
    selectedConversation,
    selectedConversationId,
    conversationError,
    isLoadingConversations,
    isLoadingConversation,
    isCreatingConversation,
    isCreatingConversationMessage,
    isUpdatingConversation,
    isUpdatingConversationMessage,
    getConversation,
    selectConversation,
    getApplicationConversations,
    getDebtConversations,
    getConversations,
    createConversation,
    createConversationMessage,
    updateConversationMessage,
    deleteConversationMessage,
    closeConversation,
    reopenConversation,
  }
}

export type UseConversationsReturn = ReturnType<typeof useConversations>

export type ConversationsTarget =
  | {
      kind: 'application'
      id: string
    }
  | {
      kind: 'debt'
      id: string
    }

export type ConversationsController = {
  target: ComputedRef<ConversationsTarget>
  conversationsList: Ref<ConversationListItem[]>
  selectedConversation: Ref<Conversation | null>
  selectedConversationId: Ref<string | null>
  conversationError: Ref<unknown>
  isLoadingConversations: Ref<boolean>
  isLoadingConversation: Ref<boolean>
  unreadCount: ComputedRef<number>
  refresh: () => Promise<void>
  selectConversation: (conversationId: string) => Promise<Conversation | void>
  createConversation: (payload: CreateConversationInput) => Promise<Conversation | void>
  createMessage: (payload: CreateConversationMessagePayload) => Promise<Conversation | void>
  updateMessage: (messageId: string, payload: UpdateConversationMessagePayload) => Promise<Conversation | void>
  deleteMessage: (messageId: string) => Promise<Conversation | void>
  toggleConversationStatus: () => Promise<Conversation | void>
}

export const useConversationsController = (
  api: UseConversationsReturn,
  getTarget: () => ConversationsTarget,
): ConversationsController => {
  const target = computed(() => getTarget())

  const refresh = async () => {
    const currentTarget = target.value
    if (!currentTarget.id) return

    const previousSelectedId = api.selectedConversationId.value

    if (currentTarget.kind === 'application') {
      await api.getApplicationConversations(currentTarget.id)
    } else {
      await api.getDebtConversations(currentTarget.id)
    }

    // Re-fetch the selected conversation to pick up new messages, but only
    // if the selection didn't change — selectDefaultConversation (called
    // internally by the list fetch above) already fetched the new one.
    if (api.selectedConversationId.value && api.selectedConversationId.value === previousSelectedId) {
      await api.getConversation(api.selectedConversationId.value)
    }
  }

  const createConversation = async (payload: CreateConversationInput) => {
    const currentTarget = target.value
    if (!currentTarget.id) return

    return api.createConversation(
      currentTarget.kind === 'application'
        ? {
            applicationId: currentTarget.id,
            ...payload,
          }
        : {
            debtId: currentTarget.id,
            ...payload,
          },
    )
  }

  const createMessage = async (payload: CreateConversationMessagePayload) => {
    const conversationId = api.selectedConversationId.value
    if (!conversationId) return

    return api.createConversationMessage(conversationId, payload)
  }

  const updateMessage = async (messageId: string, payload: UpdateConversationMessagePayload) => {
    const conversationId = api.selectedConversationId.value
    if (!conversationId) return

    return api.updateConversationMessage(conversationId, messageId, payload)
  }

  const deleteMessage = async (messageId: string) => {
    const conversationId = api.selectedConversationId.value
    if (!conversationId) return

    return api.deleteConversationMessage(conversationId, messageId)
  }

  const toggleConversationStatus = async () => {
    const conversation = api.selectedConversation.value
    if (!conversation) return

    return conversation.status === 'open'
      ? api.closeConversation(conversation.id)
      : api.reopenConversation(conversation.id)
  }

  // A closed conversation keeps its unread messages unread, but it needs no attention, so it is not counted.
  const unreadCount = computed(
    () => api.conversationsList.value.filter((c) => c.status === 'open' && c.hasUnreadMessages).length,
  )

  return {
    target,
    conversationsList: api.conversationsList,
    selectedConversation: api.selectedConversation,
    selectedConversationId: api.selectedConversationId,
    conversationError: api.conversationError,
    isLoadingConversations: api.isLoadingConversations,
    isLoadingConversation: api.isLoadingConversation,
    unreadCount,
    refresh,
    selectConversation: api.selectConversation,
    createConversation,
    createMessage,
    updateMessage,
    deleteMessage,
    toggleConversationStatus,
  }
}
